import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getAppParams, getUrlParams } from "../apiService/timespam";
import "./Theme.css";
import "./AddictionAssessment.css";
import { APIV4_BASE_URL } from "../apiService/apiClient";

const QUESTIONS = [
  { questionId: 44, category: "Alchohol", question: "Do you drink alcohol or have you ever drunk?", options: ["Yes, I currently drink Alcohol.", "No, I have never had Alcohol.", "I used to but I have quit."], scores: [0, 0, 0], endSurveyOn: [false, true, true] },
  { questionId: 45, category: "Alchohol", question: "How often do you have a drink containing alcohol?", options: ["Never", "Monthly or less", "2-4 times a month", "2-3 times a week", "4 or more times a week"], scores: [0, 1, 2, 3, 4] },
  { questionId: 46, category: "Alchohol", question: "How many standard drinks containing alcohol do you have on a typical day when drinking?", options: ["1 or 2", "3 or 4", "5 or 6", "7 to 9", "10 or more"], scores: [0, 1, 2, 3, 4] },
  { questionId: 47, category: "Alchohol", question: "How often do you have six or more drinks on one occasion?", options: ["Never", "Less than monthly", "Monthly", "Weekly", "Daily or almost daily"], scores: [0, 1, 2, 3, 4] },
  { questionId: 48, category: "Alchohol", question: "How often have you found that you were not able to stop drinking once you had started?", options: ["Never", "Less than monthly", "Monthly", "Weekly", "Daily or almost daily"], scores: [0, 1, 2, 3, 4] },
  { questionId: 49, category: "Alchohol", question: "How often have you failed to do what was normally expected of you because of drinking?", options: ["Never", "Less than monthly", "Monthly", "Weekly", "Daily or almost daily"], scores: [0, 1, 2, 3, 4] },
  { questionId: 50, category: "Alchohol", question: "How often have you needed a drink in the morning to get yourself going after a heavy drinking session?", options: ["Never", "Less than monthly", "Monthly", "Weekly", "Daily or almost daily"], scores: [0, 1, 2, 3, 4] },
  { questionId: 51, category: "Alchohol", question: "How often have you had a feeling of guilt or remorse after drinking?", options: ["Never", "Less than monthly", "Monthly", "Weekly", "Daily or almost daily"], scores: [0, 1, 2, 3, 4] },
  { questionId: 52, category: "Alchohol", question: "How often have you been unable to remember what happened the night before because you had been drinking?", options: ["Never", "Less than monthly", "Monthly", "Weekly", "Daily or almost daily"], scores: [0, 1, 2, 3, 4] },
  { questionId: 53, category: "Alchohol", question: "Have you or someone else been injured as a result of your drinking?", options: ["No", "Yes, but not in the past year", "Yes, during the past year"], scores: [0, 2, 4] },
  { questionId: 54, category: "Alchohol", question: "Has a relative or friend, doctor or other health worker been concerned about your drinking or suggested you cut down?", options: ["No", "Yes, but not in the past year", "Yes, during the past year"], scores: [0, 2, 4] },
];

const getAlcoholInfo = (s) => {
  if (s <= 7) return { label: "No Dependence", color: "#66bb6a", message: "No current alcohol-related concerns. Maintain a healthy, alcohol-free lifestyle." };
  if (s <= 15) return { label: "Low Dependence", color: "#66bb6a", message: "No significant alcohol-related issues. Continue practicing healthy drinking habits." };
  if (s <= 19) return { label: "Moderate Dependence", color: "#f7b720", message: "Potential hazardous drinking behavior. Brief counseling or monitoring is recommended." };
  if (s <= 20) return { label: "High Dependence", color: "#ff7043", message: "Likely harmful drinking patterns detected. Seek healthcare professional support." };
  return { label: "Very High Dependence", color: "#d32f2f", message: "Strong likelihood of alcohol dependence. Immediate referral for addiction treatment is advised." };
};

const ColorBar = ({ score }) => {
  const sections = [
    { label: "No Dependence", color: "#66bb6a" },
    { label: "Low Dependence", color: "#66bb6a" },
    { label: "Moderate Dependence", color: "#ffa726" },
    { label: "High Dependence", color: "#ff7043" },
    { label: "Very High Dependence", color: "#d32f2f" },
  ];
  const getActive = (s) => {
    if (s <= 7) return 0;
    if (s <= 15) return 1;
    if (s <= 19) return 2;
    if (s <= 20) return 3;
    return 4;
  };
  const active = getActive(score);
  return (
    <div className="ad-color-bar">
      {sections.map((s, i) => (
        <div key={i} className="ad-color-seg" style={{
          flex: 1,
          backgroundColor: i === active ? s.color : "var(--bg-surface)",
          borderRight: i < sections.length - 1 ? "2px solid var(--bg-card)" : "none",
        }} />
      ))}
    </div>
  );
};

const AlcoholAssessment = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const { goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType } = getAppParams();

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    if (lang === "ar") document.documentElement.dir = "rtl";
  }, [theme, lang]);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [slideKey, setSlideKey] = useState(0);
  const [answerDisabled, setAnswerDisabled] = useState(false);
  const [isFinishEnabled, setIsFinishEnabled] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [view, setView] = useState("quiz");

  const calcScore = (ans) =>
    Object.keys(ans).reduce((sum, k) => sum + QUESTIONS[k].scores[ans[k]], 0);

  const handleAnswerChange = (optionIdx) => {
    if (answerDisabled) return;
    setAnswerDisabled(true);
    setTimeout(() => setAnswerDisabled(false), 500);

    const newAnswers = { ...answers, [currentIdx]: optionIdx };
    setAnswers(newAnswers);

    if (currentIdx === 0 && (optionIdx === 1 || optionIdx === 2)) {
      setIsFinishEnabled(true);
      return;
    } else {
      setIsFinishEnabled(false);
    }

    setTimeout(() => {
      if (currentIdx < QUESTIONS.length - 1) {
        setCurrentIdx((p) => p + 1);
        setSlideKey((k) => k + 1);
      } else {
        setTotalScore(calcScore(newAnswers));
      }
    }, 300);
  };

  const handleNext = () => {
    if (currentIdx < QUESTIONS.length - 1) {
      setCurrentIdx((p) => p + 1);
      setSlideKey((k) => k + 1);
    } else {
      setTotalScore(calcScore(answers));
    }
  };

  const handlePrevious = () => {
    if (currentIdx > 0) {
      setCurrentIdx((p) => p - 1);
      setSlideKey((k) => k + 1);
    }
  };

  const handleFinish = () => {
    const finalScore = calcScore(answers);
    const label = getAlcoholInfo(finalScore).label;

    const preparedQuestions = Object.keys(answers).map((k) => ({
      questionId: QUESTIONS[k].questionId,
      question: "",
      answer: answers[k],
      score: QUESTIONS[k].scores[answers[k]],
      category: QUESTIONS[k].category,
    }));

    const scoreData = {
      mentalWellnessScore: finalScore,
      mentalWellnessLabel: label,
      anxietyScore: 0,
      depressionScore: 0,
      anxietyLabel: "",
      depressionLabel: "",
    };

    const qs = new URLSearchParams({
      questions: JSON.stringify(preparedQuestions),
      scoreData: JSON.stringify(scoreData),
      goqiiUserId, nonce, signature, apiKey, goqiiAccessToken, appVersion, appType,
      type: "addiction",
      subType: "alcohol",
    }).toString();

    fetch(`${APIV4_BASE_URL}/lab/save_mental_health_assessment?${qs}`).catch(console.error);
    setTotalScore(finalScore);
    setTimeout(() => setView("result"), 1000);
  };

  const progress =
    currentIdx === 0 && (answers[0] === 1 || answers[0] === 2)
      ? 100
      : ((currentIdx + 1) / QUESTIONS.length) * 100;

  const canFinish = isFinishEnabled || currentIdx === QUESTIONS.length - 1;

  /* ---- RESULT VIEW ---- */
  if (view === "result") {
    const info = getAlcoholInfo(totalScore);
    return (
      <div className="ad-wrapper">
        <div className="ad-result-card">
          <p className="ad-result-title">Your Alcohol Addiction Assessment Results</p>

          <p className="ad-result-section-title" style={{ textAlign: "center" }}>Alcohol Addiction Risk</p>
          <p className="ad-score-text" style={{ color: info.color, textAlign: "center" }}>{info.label} (Score: {totalScore}/40)</p>
          <ColorBar score={totalScore} />
          <hr className="ad-divider" />
          <p className="ad-score-note" style={{ textAlign: "center" }}>{info.message}</p>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <button className="ad-btn-primary" style={{ width: "auto", padding: "10px 32px" }} onClick={() => {
              window.location.href = `/addiction_assessment${window.location.search}`;
            }}>{t("addictionAssessment.close")}</button>
          </div>
        </div>
      </div>
    );
  }

  /* ---- QUIZ VIEW ---- */
  const q = QUESTIONS[currentIdx];
  return (
    <div className="ad-wrapper">
      <p className="ad-quiz-intro">During the Past Year,</p>
      <div key={slideKey} className="ad-question-card">
        <p className="ad-question-text">{currentIdx + 1}: {q.question}</p>
        <div className="ad-options">
          {q.options.map((opt, i) => (
            <label
              key={i}
              className={`ad-option${answers[currentIdx] === i ? " selected" : ""}`}
              onClick={() => handleAnswerChange(i)}
            >
              <input type="radio" name={`q${currentIdx}`} checked={answers[currentIdx] === i} onChange={() => {}} />
              <span className="ad-option-label">{opt}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="ad-quiz-footer">
        <button className="ad-btn-outline" onClick={handlePrevious} disabled={currentIdx === 0}>{t("addictionAssessment.previous")}</button>
        <div className="ad-progress-wrap">
          <div className="ad-progress-bar" style={{ width: `${progress}%` }} />
        </div>
        {canFinish ? (
          <button className="ad-btn-contained" onClick={handleFinish} disabled={answers[currentIdx] === undefined}>{t("addictionAssessment.finish")}</button>
        ) : (
          <button className="ad-btn-contained" onClick={handleNext} disabled={answers[currentIdx] === undefined}>{t("addictionAssessment.next")}</button>
        )}
      </div>
    </div>
  );
};

export default AlcoholAssessment;
